import {
  useSalesServiceSalesOrdersMediaCreate,
  useSalesServiceSalesOrdersMediaListKey,
  useSalesServiceSalesOrdersRetrieveKey
} from '@insurance/api/queries'
import {
  OrderMediaCreate,
  OrderMediaCreateCategoryEnum,
  OrderMediaDetail as OrderMediaGet
} from '@insurance/api/requests'
import { Text } from '@shared/components'
import { WithPagination } from '@shared/types'
import { replaceEmptyWithUndefined, showApiErrors } from '@shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { Flex, Input, Modal, Select, Spin, UploadFile } from 'antd'
import Dragger from 'antd/es/upload/Dragger'
import { DocumentUpload } from 'iconsax-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  orderId?: string | number
  onDisabledFileGet?: (disabled: boolean) => void
  onSuccess?: () => void
  /**
   * When supplied, the notes modal asks which kind of document this is and
   * sends it as `category`. Products whose case file distinguishes kinds
   * (guarantees) pass this; everyone else keeps the backend's `order` default.
   */
  categories?: OrderMediaCreateCategoryEnum[]
  /** i18n key per category value, used to label the select options. */
  categoryLabelKey?: Record<string, string>
}

export const AddOrderMediaFields = ({
  orderId,
  onDisabledFileGet,
  onSuccess,
  categories,
  categoryLabelKey
}: Props) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [notes, setNotes] = useState('')
  const [category, setCategory] = useState<
    OrderMediaCreateCategoryEnum | undefined
  >()
  const [files, setFiles] = useState<UploadFile[]>([])
  const [openModal, setOpenModal] = useState(false)

  const onCloseAddMedia = () => {
    setNotes('')
    setCategory(undefined)
    setFiles([])
    setOpenModal(false)
  }

  useEffect(() => {
    onDisabledFileGet?.(!!files.length)
  }, [!!files.length])

  const { mutateAsync: addMedia, isPending: isAddMediaPending } =
    useSalesServiceSalesOrdersMediaCreate({
      onSuccess: (data, variable) => {
        let orderMediaList: OrderMediaGet[] = []

        queryClient.setQueryData(
          [useSalesServiceSalesOrdersMediaListKey, { order: Number(orderId) }],
          (oldData: WithPagination<OrderMediaGet>) => {
            const results = oldData?.results || []
            const count = oldData?.count || 0

            orderMediaList = [
              ...results,
              {
                ...data,
                media: {
                  id: Number(data?.media),
                  file: {
                    headers: {},
                    url: window.URL.createObjectURL(
                      variable?.formData?.file as unknown as Blob
                    )
                  },
                  name: variable?.formData?.name,
                  // The read model nests the category under `media`, while the
                  // write model carries it at the root — without echoing it here
                  // the optimistic row shows no category until a real refetch.
                  category: data?.category ?? variable?.formData?.category
                }
              } as unknown as OrderMediaGet
            ]

            return {
              ...oldData,
              count: count + 1,
              results: orderMediaList
            } as WithPagination<OrderMediaGet>
          }
        )
      },
      onError: (error) => showApiErrors(error)
    })

  const onAddMedia = async () => {
    if (!orderId || !files.length) return
    if (categories?.length && !category) return

    setOpenModal(false)

    await Promise.all(
      files.map((file) => {
        return addMedia({
          formData: {
            file: file.originFileObj as unknown as string,
            ...replaceEmptyWithUndefined({
              order: Number(orderId),
              name: file.name,
              notes,
              category
            })
          } as OrderMediaCreate
        })
      })
    )

    // The order carries document-derived state — the guarantees case file
    // exposes `missing_documents` and mirrors the same gap in `warnings` — so a
    // stale order would keep listing a document that was just attached.
    queryClient.invalidateQueries({
      queryKey: [useSalesServiceSalesOrdersRetrieveKey, { id: Number(orderId) }]
    })

    onCloseAddMedia()
    onSuccess?.()
  }

  return (
    <>
      <Spin spinning={isAddMediaPending}>
        <Dragger
          multiple
          onChange={({ fileList }) => {
            setFiles(fileList)
            setOpenModal(true)
          }}
          fileList={[]}
          customRequest={({ onSuccess }) => onSuccess?.({})}
        >
          <Flex vertical gap={10}>
            <Flex justify="center" align="center" gap={10}>
              <DocumentUpload size={20} />

              <Text>{t('upload_files')}</Text>
            </Flex>

            <Text>{t('upload_files_text')}</Text>
          </Flex>
        </Dragger>
      </Spin>

      <Modal
        title={t('notes')}
        open={openModal}
        okText={t('save')}
        okButtonProps={{
          loading: isAddMediaPending,
          disabled: !!categories?.length && !category
        }}
        onCancel={onCloseAddMedia}
        onOk={onAddMedia}
      >
        <Flex vertical gap={12}>
          {!!categories?.length && (
            <Select
              value={category}
              placeholder={t('document_category')}
              options={categories.map((value) => ({
                value,
                label: categoryLabelKey?.[value]
                  ? t(categoryLabelKey[value])
                  : value
              }))}
              onChange={setCategory}
            />
          )}

          <Input.TextArea
            value={notes}
            placeholder={t('notes')}
            autoSize={{ minRows: 4 }}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Flex>
      </Modal>
    </>
  )
}
